/**
 * Экран «Настройки» (`/dashboard/settings`): профиль (имя и фамилия, email,
 * смена пароля), уведомления (звук, вибрация, push), организация и выход.
 *
 * Собирается из существующих API: профиль читается из `GET /users/me`
 * (через AuthContext), имя сохраняется `PATCH /v1/users/me`, пароль —
 * `POST /v1/users/me/password`, закрепление организации —
 * `POST /users/default-org` (через OrgContext). Настройки звука, вибрации и
 * push — клиентские: они лежат в localStorage и читаются обработчиком
 * веб-сокета, поэтому переключатель срабатывает без переподключения.
 *
 * Выход переехал сюда из шапки списка чатов: в «Профиле» его больше нет.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import { LocalNotifications } from '@capacitor/local-notifications';
import axiosClient from '../api/axiosClient';
import { apiErrorMessage } from '../api/errors';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { User } from '../types';
import {
    NotificationPrefs,
    readNotificationPrefs,
    writeNotificationPrefs,
} from '../components/Settings/notificationPrefs';
import {
    getPushState,
    PushState,
    subscribeToPush,
    unsubscribeFromPush,
} from '../utils/pushSubscription';

/** Версия приложения: та же, что показывает диагностика на бэкенде. */
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.2';

// Названия организаций приходят с эмодзи-префиксом («🏢 КОМПАНИЯ»,
// «🔒 МОЙ ЛИЧНЫЙ ГРАФИК»). В настройках иконка раздела лишняя.
const displayOrgName = (name: string): string =>
    name.replace(/^[^0-9A-Za-zА-Яа-яЁё]+/, '').trim() || name;

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
    >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            {title}
        </Typography>
        {children}
    </Paper>
);

const SettingsPage: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => {
    const navigate = useNavigate();
    const { user, logout, updateUser } = useAuth();
    const { orgId, organizations, pinnedOrgId, setOrg } = useOrg();

    // Профиль
    const [firstName, setFirstName] = useState(user?.firstName ?? '');
    const [lastName, setLastName] = useState(user?.lastName ?? '');
    const [nameBusy, setNameBusy] = useState(false);
    const [nameError, setNameError] = useState('');
    const [nameNotice, setNameNotice] = useState('');

    // Пароль
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [passwordBusy, setPasswordBusy] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordNotice, setPasswordNotice] = useState('');

    // Уведомления: значения читаем один раз при открытии экрана.
    const [prefs, setPrefs] = useState<NotificationPrefs>(() => readNotificationPrefs());
    // Push — не только флаг в localStorage: подписка живёт в браузере, поэтому
    // состояние переключателя читаем у service worker'а.
    const [pushState, setPushState] = useState<PushState>('unsupported');
    const [pushHint, setPushHint] = useState<{ text: string; severity: 'success' | 'warning' } | null>(
        null
    );
    const [pushBusy, setPushBusy] = useState(false);

    // Организация
    const [orgBusy, setOrgBusy] = useState(false);
    const [orgError, setOrgError] = useState('');
    const [orgNotice, setOrgNotice] = useState('');

    // Профиль приходит в контекст асинхронно (после перезагрузки страницы
    // сначала отдаётся кэш localStorage), поэтому подставляем имя, когда
    // данные действительно появились.
    useEffect(() => {
        setFirstName(user?.firstName ?? '');
        setLastName(user?.lastName ?? '');
    }, [user?.firstName, user?.lastName]);

    // Подписка могла остаться с прошлого запуска (или её нет вовсе) — подтягиваем
    // реальное состояние, а не только флаг из localStorage.
    useEffect(() => {
        let active = true;
        getPushState()
            .then((state) => {
                if (!active) return;
                setPushState(state);
                if (state === 'subscribed') {
                    // push включён в браузере — пусть и в приложении будет включён.
                    setPrefs((current) => {
                        if (current.push) return current;
                        const next = { ...current, push: true };
                        writeNotificationPrefs(next);
                        return next;
                    });
                }
            })
            .catch(() => undefined);
        return () => {
            active = false;
        };
    }, []);

    const savePrefs = (next: NotificationPrefs) => {
        setPrefs(next);
        writeNotificationPrefs(next);
    };

    const handleSaveName = async () => {
        if (!user) return;

        // Отправляем только реально изменённые поля: у части пользователей
        // фамилия пустая, и «эхо» пустого значения сервер отклонил бы как
        // попытку стереть поле.
        const payload: { firstName?: string; lastName?: string } = {};
        if (firstName !== (user.firstName ?? '')) payload.firstName = firstName;
        if (lastName !== (user.lastName ?? '')) payload.lastName = lastName;

        setNameError('');
        setNameNotice('');
        if (Object.keys(payload).length === 0) {
            setNameNotice('Изменений нет.');
            return;
        }

        setNameBusy(true);
        try {
            const response = await axiosClient.patch('/v1/users/me', payload);
            // Ответ — тот же профиль, что отдаёт GET /users/me, поэтому просто
            // обновляем им кэш: имя в шапке и в чатах меняется сразу.
            updateUser({ ...(user as User), ...response.data });
            setNameNotice('Имя сохранено.');
        } catch (error) {
            setNameError(apiErrorMessage(error, 'Не удалось сохранить имя.'));
        } finally {
            setNameBusy(false);
        }
    };

    const handleChangePassword = async () => {
        setPasswordBusy(true);
        setPasswordError('');
        setPasswordNotice('');
        try {
            const response = await axiosClient.post('/v1/users/me/password', {
                currentPassword,
                newPassword,
            });
            setPasswordNotice(response.data?.message || 'Пароль изменён.');
            setCurrentPassword('');
            setNewPassword('');
        } catch (error) {
            setPasswordError(apiErrorMessage(error, 'Не удалось сменить пароль.'));
        } finally {
            setPasswordBusy(false);
        }
    };

    /**
     * Спросить разрешение на уведомления. На Android — через Capacitor,
     * в браузере — через Notification API. Молча ничего не запрашиваем:
     * вызов происходит только по явному переключению.
     */
    const requestPushPermission = async (): Promise<boolean> => {
        try {
            if (navigator.userAgent.includes('Android')) {
                const result = await LocalNotifications.requestPermissions();
                return result.display === 'granted';
            }
            if ('Notification' in window) {
                const result = await Notification.requestPermission();
                return result === 'granted';
            }
        } catch (error) {
            console.warn('Push permission error:', error);
        }
        return false;
    };

    const handlePushToggle = async (enabled: boolean) => {
        setPushBusy(true);
        setPushHint(null);
        try {
            if (!enabled) {
                const result = await unsubscribeFromPush();
                savePrefs({ ...prefs, push: false });
                setPushState('unsubscribed');
                setPushHint({ text: result.message, severity: 'warning' });
                return;
            }

            const granted = await requestPushPermission();
            if (!granted) {
                savePrefs({ ...prefs, push: false });
                setPushState('unsubscribed');
                setPushHint({
                    text: 'Браузер не дал разрешение на уведомления. Разрешите их в настройках браузера для этого сайта.',
                    severity: 'warning',
                });
                return;
            }

            const result = await subscribeToPush({ permissionGranted: true });
            savePrefs({ ...prefs, push: result.ok });
            setPushState(result.ok ? 'subscribed' : 'unsubscribed');
            setPushHint({ text: result.message, severity: result.ok ? 'success' : 'warning' });
        } catch (error) {
            // subscribe/unsubscribe сами не бросают, но подстрахуемся: экран
            // настроек не должен падать из-за уведомлений.
            savePrefs({ ...prefs, push: false });
            setPushState('unsubscribed');
            setPushHint({
                text: 'Не удалось изменить настройку push-уведомлений. Попробуйте ещё раз.',
                severity: 'warning',
            });
        } finally {
            setPushBusy(false);
        }
    };

    const handlePinOrganization = async () => {
        if (!orgId) {
            setOrgError('Сначала выберите организацию, потом закрепляйте.');
            setOrgNotice('');
            return;
        }
        setOrgBusy(true);
        setOrgError('');
        setOrgNotice('');
        try {
            await setOrg(orgId, { pin: true });
            setOrgNotice('Организация закреплена: с неё начинается работа.');
        } catch (error) {
            setOrgError(apiErrorMessage(error, 'Не удалось закрепить организацию.'));
        } finally {
            setOrgBusy(false);
        }
    };

    const handleSwitchOrganization = async (nextOrgId: string) => {
        setOrgBusy(true);
        setOrgError('');
        setOrgNotice('');
        try {
            await setOrg(nextOrgId);
        } catch (error) {
            setOrgError(apiErrorMessage(error, 'Не удалось выбрать организацию.'));
        } finally {
            setOrgBusy(false);
        }
    };

    return (
        <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: '#f4f7f6' }}>
            <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, sm: 3 }, display: 'grid', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isMobile && (
                        <IconButton onClick={() => navigate('/dashboard')} title="Назад к чатам">
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Настройки
                    </Typography>
                </Box>

                <Section title="Профиль">
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <TextField
                            label="Имя"
                            value={firstName}
                            onChange={(event) => setFirstName(event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Фамилия"
                            value={lastName}
                            onChange={(event) => setLastName(event.target.value)}
                            fullWidth
                            helperText="Можно не заполнять: сохраняются только изменённые поля."
                        />
                        <TextField
                            label="Email"
                            value={user?.email ?? ''}
                            fullWidth
                            disabled
                            helperText="Email меняет администратор компании."
                        />

                        {nameError && <Alert severity="error">{nameError}</Alert>}
                        {nameNotice && <Alert severity="success">{nameNotice}</Alert>}

                        <Box>
                            <Button
                                variant="contained"
                                onClick={handleSaveName}
                                disabled={nameBusy || !user}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {nameBusy ? 'Сохраняем…' : 'Сохранить имя'}
                            </Button>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Typography variant="body2" color="text.secondary">
                            Смена пароля: укажите текущий пароль и новый — не короче 6 символов.
                        </Typography>
                        <TextField
                            label="Текущий пароль"
                            type="password"
                            value={currentPassword}
                            onChange={(event) => setCurrentPassword(event.target.value)}
                            fullWidth
                            autoComplete="current-password"
                        />
                        <TextField
                            label="Новый пароль"
                            type="password"
                            value={newPassword}
                            onChange={(event) => setNewPassword(event.target.value)}
                            fullWidth
                            autoComplete="new-password"
                        />

                        {passwordError && <Alert severity="error">{passwordError}</Alert>}
                        {passwordNotice && <Alert severity="success">{passwordNotice}</Alert>}

                        <Box>
                            <Button
                                variant="outlined"
                                onClick={handleChangePassword}
                                disabled={passwordBusy || !currentPassword || !newPassword}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {passwordBusy ? 'Меняем…' : 'Сменить пароль'}
                            </Button>
                        </Box>
                    </Box>
                </Section>

                <Section title="Уведомления">
                    <Box sx={{ display: 'grid', gap: 0.5 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={prefs.sound}
                                    onChange={(event) =>
                                        savePrefs({ ...prefs, sound: event.target.checked })
                                    }
                                />
                            }
                            label="Звук нового сообщения"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={prefs.vibration}
                                    onChange={(event) =>
                                        savePrefs({ ...prefs, vibration: event.target.checked })
                                    }
                                />
                            }
                            label="Вибрация"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={pushState === 'subscribed'}
                                    disabled={pushState === 'unsupported' || pushBusy}
                                    onChange={(event) => handlePushToggle(event.target.checked)}
                                />
                            }
                            label="Push-уведомления"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {pushState === 'unsupported'
                                ? 'Этот браузер не поддерживает push-уведомления: уведомления придут только при открытом приложении.'
                                : 'Уведомления при закрытом приложении: разрешение спрашивает браузер, подписка сохраняется на сервере.'}
                        </Typography>
                        {pushHint && (
                            <Alert severity={pushHint.severity} sx={{ mt: 1 }}>
                                {pushHint.text}
                            </Alert>
                        )}
                    </Box>
                </Section>

                <Section title="Организация">
                    {organizations.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Организаций пока нет.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Закреплённая организация открывается первой. Текущая: {orgId
                                    ? displayOrgName(
                                          organizations.find((org) => org.id === orgId)?.name ?? ''
                                      )
                                    : 'все организации'}
                                .
                            </Typography>

                            <List dense disablePadding>
                                {organizations.map((org) => (
                                    <ListItemButton
                                        key={org.id}
                                        selected={org.id === orgId}
                                        disabled={orgBusy}
                                        onClick={() => handleSwitchOrganization(org.id)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        <ListItemText
                                            primary={displayOrgName(org.name)}
                                            secondary={pinnedOrgId === org.id ? 'Закреплена' : undefined}
                                        />
                                        {pinnedOrgId === org.id && (
                                            <Chip label="По умолчанию" size="small" color="primary" />
                                        )}
                                    </ListItemButton>
                                ))}
                            </List>

                            {orgError && <Alert severity="error">{orgError}</Alert>}
                            {orgNotice && <Alert severity="success">{orgNotice}</Alert>}

                            <Box>
                                <Button
                                    variant="outlined"
                                    onClick={handlePinOrganization}
                                    disabled={orgBusy || !orgId}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Закрепить текущую организацию
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Section>

                <Section title="О приложении">
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            ORION, версия {APP_VERSION}
                        </Typography>
                        <Box>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<LogoutIcon />}
                                onClick={logout}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Выйти из аккаунта
                            </Button>
                        </Box>
                    </Box>
                </Section>
            </Box>
        </Box>
    );
};

export default SettingsPage;
